import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const reset = [
  { letter: "R", title: "Reflect", desc: "Honor the week you just lived; understand what worked and what needs attention." },
  { letter: "E", title: "Empty", desc: "Release the mental load and unnecessary pressure." },
  { letter: "S", title: "Select", desc: "Choose the priorities that matter most; a time and energy audit." },
  { letter: "E", title: "Establish", desc: "Create systems that support success — planning, boundaries, and family rhythms." },
  { letter: "T", title: "Thrive", desc: "Move forward intentionally — self-care, leadership reflection, and gratitude." },
];

const pillars = [
  { n: "01", title: "Thrive", desc: "Personal growth and well-being — confidence, energy, self-awareness, and sustainable habits." },
  { n: "02", title: "Mother", desc: "Intentional family leadership — connection, presence, family systems, and meaningful moments." },
  { n: "03", title: "Lead", desc: "Professional growth and impact — career strategy, leadership development, confidence, and influence." },
  { n: "04", title: "Connect", desc: "Community and support — relationships, mentorship, and shared experiences." },
];

const valueAreas = ["Advancement", "Compensation", "Scope", "Visibility", "Flexibility", "Opportunity"];

export default function Method() {
  return (
    <main data-testid="method-page">
      {/* HERO */}
      <section className="pt-40 md:pt-52 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-8">The Method</p>
            <h1 className="font-serif font-light text-wine text-4xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight max-w-4xl">
              A model for sustainable success — built on outcomes, not hours.
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-charcoal/80 leading-relaxed">
              Everything we create rests on two ideas: a new definition of the modern professional, and a
              repeatable weekly practice for living it. Together they turn ambition into something sustainable.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OUTCOME-LED PROFESSIONAL */}
      <section className="py-20 md:py-28" data-testid="outcome-led-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta">The Framework</p>
              <h2 className="font-serif font-light text-wine text-3xl md:text-4xl mt-4 leading-tight">
                The Outcome-led Professional™
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-charcoal/80 leading-relaxed">
            <Reveal>
              <p className="font-serif font-light text-2xl md:text-3xl text-wine leading-snug">
                Someone who delivers exceptional value through outcomes, trust, and impact rather than by
                visibility or hours worked.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                Most workplaces still reward presence. For an ambitious mother, that math is impossible — and it
                was always the wrong math. When your time is genuinely finite, you stop confusing motion with
                progress: you get clear about priorities, you build trust so your work speaks when you&rsquo;re
                not in the room, and you lead through clarity rather than availability.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      {/* OUTCOMES CREATE VALUE */}
      <section className="py-24 md:py-32 bg-cream" data-testid="outcomes-create-value-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta">Outcomes Create Value</p>
              <h2 className="font-serif font-light text-wine text-3xl md:text-4xl mt-4 leading-tight">
                Know the impact you create.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-charcoal/80 leading-relaxed">
            <Reveal>
              <p>
                Being outcome-led changes more than how you manage your time. It changes how you understand —
                and communicate — the value of your leadership. When you&rsquo;re clear about the outcomes you
                create, you can become more intentional about how you position and advocate for that value in
                conversations around:
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="flex flex-wrap gap-x-3 gap-y-3 pt-2" data-testid="value-areas-list">
                {valueAreas.map((area) => (
                  <li
                    key={area}
                    className="inline-flex items-center rounded-full border border-gold/50 bg-blush/20 px-5 py-2 text-sm tracking-wide text-wine"
                  >
                    {area}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-charcoal/70">
                This is an area explored in depth through{" "}
                <Link
                  to="/lead"
                  data-testid="value-coaching-link"
                  className="text-terracotta font-medium underline underline-offset-4 hover:text-wine transition-colors"
                >
                  private leadership &amp; career strategy coaching
                </Link>
                .
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RESET METHOD */}
      <section className="bg-blush/30 py-24 md:py-32" data-testid="method-reset-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">Intentionality In Practice</p>
            <h2 className="font-serif font-light text-wine text-4xl md:text-5xl max-w-3xl leading-tight">
              The RESET Method™ — five weekly movements.
            </h2>
            <p className="mt-8 max-w-2xl text-lg text-charcoal/80 leading-relaxed">
              Outcome-led leadership starts with clarity about what matters. The RESET Method™ creates a
              repeatable practice for bringing that same intentionality to the week ahead.
            </p>
          </Reveal>
          <div className="mt-16 divide-y divide-gold/40 border-t border-gold/40">
            {reset.map((r, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 items-baseline">
                  <div className="md:col-span-1">
                    <span className="font-serif text-4xl md:text-5xl text-terracotta">{r.letter}</span>
                  </div>
                  <div className="md:col-span-3">
                    <h3 className="font-serif text-2xl md:text-3xl text-wine">{r.title}</h3>
                  </div>
                  <div className="md:col-span-8">
                    <p className="text-charcoal/75 leading-relaxed md:text-lg">{r.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE WHOLE-LIFE LENS */}
      <section className="py-24 md:py-32" data-testid="method-pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">The Whole-Life Lens</p>
            <h2 className="font-serif font-light text-wine text-4xl md:text-5xl max-w-3xl leading-tight">
              Thrive. Mother. Lead. Connect.
            </h2>
            <p className="mt-8 max-w-2xl text-lg text-charcoal/80 leading-relaxed">
              Sustainable success isn't created in one dimension of life. The T&amp;M approach considers four
              interconnected areas that shape how we lead and live.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-gold/30 border border-gold/30">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="bg-cream p-10 md:p-14 h-full">
                  <span className="font-serif text-gold text-2xl">{p.n}</span>
                  <h3 className="font-serif text-wine text-3xl md:text-4xl mt-6">{p.title}</h3>
                  <p className="mt-5 text-charcoal/75 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PUT THE APPROACH INTO PRACTICE — SINGLE CTA */}
      <section className="bg-wine text-cream py-24 md:py-32" data-testid="method-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">
              Ready To Put The Approach Into Practice?
            </p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">
              Explore private coaching with Jenna.
            </h2>
            <div className="mt-12">
              <Link
                to="/lead"
                data-testid="method-explore-coaching-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-cream text-wine px-10 py-4 text-sm font-medium tracking-wide hover:bg-gold hover:text-wine transition-colors duration-300"
              >
                Explore 1:1 Coaching
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <p className="mt-10 text-sm text-cream/60">
              Prefer to start with practical resources?{" "}
              <Link
                to="/shop"
                data-testid="method-shop-link"
                className="text-cream/80 underline underline-offset-4 hover:text-cream transition-colors"
              >
                Explore Resources
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
